import { Component, Input } from '@angular/core';
import { MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatLabel } from '@angular/material/form-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TripRouteService } from 'app/modules/components/sales/trip/trip.service';
import { HttpErrorResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';

const atLeastOneCheckboxCheckedValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
  const group = control as FormGroup;
  const includePreSales = group.get('includePreSales')?.value;
  const includeDeliveries = group.get('includeDeliveries')?.value;
  const includePayments = group.get('includePayments')?.value;
  const includeEffectiveness = group.get('includeEffectiveness')?.value;

  if (includePreSales || includeDeliveries || includePayments || includeEffectiveness) {
    return null; // El formulario es válido
  }

  return { 'atLeastOneChecked': true }; // El formulario es inválido
};

@Component({
  selector: 'app-download-resume-routes-monitor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogActions,
    MatIcon,
    MatRadioButton,
    MatRadioGroup,
    MatLabel,
    MatCheckbox,
    MatDialogContent,
    FormSectionComponent,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './download-resume-routes-monitor.component.html',
  styleUrl: './download-resume-routes-monitor.component.scss'
})

export class DownloadResumeRoutesMonitorComponent {
  @Input() tripId: number;

  loading = false;
  resumeForm: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<DownloadResumeRoutesMonitorComponent>,
    private fb: FormBuilder,
    private tripService: TripRouteService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.resumeForm = this.fb.group({
      includePreSales: [false],
      includeDeliveries: [false],
      includePayments: [false],
      includeEffectiveness: [false],
      showVisitTableMode: ['none']
    }, { validators: atLeastOneCheckboxCheckedValidator });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onDownloadExcel(): Promise<void> {
    this.loading = true;

    if (!this.resumeForm.valid) {
      this.loading = false;
      this.snackBar.open('Por favor, selecciona al menos una opción.', 'Cerrar', { duration: 3000 });
      return;
    }

    const data = {
      tripId: this.tripId,
      includeDeliveries: this.resumeForm.get("includeDeliveries")?.value,
      includePreSales: this.resumeForm.get("includePreSales")?.value,
      includePayments: this.resumeForm.get("includePayments")?.value,
      includeEffectiveness: this.resumeForm.get("includeEffectiveness")?.value,
      showVisitTableMode: this.resumeForm.get("showVisitTableMode")?.value
    };

    try {
      const response: Blob = await firstValueFrom(this.tripService.downloadResumesExcel(data));

      const filename = `Resumen de viaje - ${this.tripId}.xlsx`;
      saveAs(response, filename);

      this.snackBar.open('¡Descarga realizada con éxito!', 'Cerrar', { duration: 3000 });
      this.dialogRef.close();
    } catch (error) {
      let errorMessage = 'Error al descargar el Excel.';

      if (error.error instanceof Blob) {
        try {
          const text = await error.error.text();
          const errJson = JSON.parse(text);
          errorMessage = errJson.message || errorMessage;
        } catch { }
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      }

      this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }

  async onDownload(): Promise<void> {
    this.loading = true;

    if (!this.resumeForm.valid) {
      this.loading = false;
      this.snackBar.open('Por favor, selecciona al menos una opción.', 'Cerrar', { duration: 3000 });
      return;
    }

    const data = {
      tripId: this.tripId,
      includeDeliveries: this.resumeForm.get("includeDeliveries")?.value,
      includePreSales: this.resumeForm.get("includePreSales")?.value,
      includePayments: this.resumeForm.get("includePayments")?.value,
      includeEffectiveness: this.resumeForm.get("includeEffectiveness")?.value,
      showVisitTableMode: this.resumeForm.get("showVisitTableMode")?.value
    };

    try {
      const response: Blob = await firstValueFrom(this.tripService.downloadResumesPDF(data));

      const filename = `resumen-ruta-${this.tripId}.pdf`;
      saveAs(response, filename);

      this.snackBar.open('¡Descarga realizada con éxito!', 'Cerrar', { duration: 3000 });
      this.dialogRef.close();
    } catch (error) {
      let errorMessage = 'Error al descargar el PDF.';

      if (error.error instanceof Blob) {
        try {
          const text = await error.error.text();
          const errJson = JSON.parse(text);
          errorMessage = errJson.message || errorMessage;
        } catch { }
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      }

      this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }

}
